import { doc, setDoc } from "firebase/firestore";
import { db } from "../../Firebase";
import {
    kSequencesCollectionName,
    kSequenceLayersSubcollectionName,
    kSequenceLayerClipsSubcollectionName,
} from "../../utils/CollectionConstants";
import { IsVideoTrackType } from "../../utils/utils";

const nowUnixSec = () => Math.floor(Date.now() / 1000);

const stripExt = (name: string): string => name.replace(/\.[^/.]+$/, "");

const normalizeName = (name: string): string =>
    stripExt(String(name ?? ""))
        .replace(/[_-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const clampTitle = (title: string, maxLen: number = 64): string => {
    const t = String(title ?? "").trim();
    if (t.length <= maxLen) return t;
    return t.slice(0, Math.max(0, maxLen - 1)).trimEnd() + "…";
};

const longestCommonPrefix = (values: string[]): string => {
    const list = values.filter(Boolean);
    if (list.length === 0) return "";
    const lower = list.map((v) => v.toLowerCase());
    const first = lower[0];
    let i = 0;
    outer: for (; i < first.length; i++) {
        const ch = first[i];
        for (let j = 1; j < lower.length; j++) {
            if (lower[j][i] !== ch) break outer;
        }
    }
    const prefixRaw = list[0].slice(0, i);
    return prefixRaw.replace(/[\s._-]+$/g, "").trim();
};

export const deriveSequenceTitleFromFileNames = (fileNames: string[]): string => {
    const bases = Array.from(
        new Set(
            (fileNames ?? [])
                .map((n) => normalizeName(n))
                .filter((n) => n.length > 0),
        ),
    );

    if (bases.length === 0) return "Recording";
    if (bases.length === 1) return clampTitle(bases[0]);
    if (bases.length === 2) return clampTitle(`${bases[0]} & ${bases[1]}`);

    const common = longestCommonPrefix(bases);
    if (common.length >= 8) return clampTitle(common);

    return clampTitle(`${bases[0]} + ${bases.length - 1} more`);
};

export const deriveSequenceTitleFromFiles = (files: File[]): string =>
    deriveSequenceTitleFromFileNames((files ?? []).map((f) => f?.name ?? ""));

const getTrackDurationSeconds = (track: any): number => {
    const raw =
        track?.duration ??
        track?.Duration ??
        track?.durationSec ??
        track?.durationSecs ??
        track?.media?.durationSec;
    const parsed = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const buildId = (prefix: string) =>
    crypto?.randomUUID?.() ?? `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const parseRecordingStartMs = (value: unknown): number | null => {
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

export async function populateSequenceFromTracks(args: {
    sequenceId: string;
    projectId: string;
    userId: string;
    workspaceId?: string | null;
    tracks: any[];
    layerNames?: string[];
}): Promise<{ orderedLayerIds: string[] }> {
    const sequenceId = String(args.sequenceId ?? "").trim();
    const projectId = String(args.projectId ?? "").trim();
    const userId = String(args.userId ?? "").trim();
    const workspaceId = args.workspaceId ?? null;
    const tracks = Array.isArray(args.tracks) ? args.tracks : [];
    const layerNames = Array.isArray(args.layerNames) ? args.layerNames : [];

    if (!sequenceId || !projectId || !userId || tracks.length === 0) {
        return { orderedLayerIds: [] };
    }

    const nowSec = nowUnixSec();
    const sequenceRef = doc(db, kSequencesCollectionName, sequenceId);

    const sourceEntries = tracks
        .map((track: any, index: number) => {
            const trackId = String(track?.trackId || track?.id || "");
            if (!trackId) return null;
            const nameCandidate = String(layerNames[index] ?? track?.title ?? track?.name ?? `Track ${index + 1}`);
            const recordingStartMs = parseRecordingStartMs(track?.recordingStartMs);
            return {
                index,
                track,
                trackId,
                layerId: buildId("layer"),
                clipId: buildId("clip"),
                name: nameCandidate,
                type: IsVideoTrackType(track?.trackType) ? "video" : "audio",
                durationSec: getTrackDurationSeconds(track),
                recordingStartMs,
            };
        })
        .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

    if (sourceEntries.length === 0) return { orderedLayerIds: [] };

    const recordingStartBaselines = sourceEntries
        .map((entry) => entry.recordingStartMs)
        .filter((value): value is number => value !== null);
    const earliestRecordingStartMs =
        recordingStartBaselines.length > 0 ? Math.min(...recordingStartBaselines) : null;

    const orderedLayerIds = sourceEntries.map((entry) => entry.layerId);

    // Update the sequence doc with layer ordering (do not touch userId/workspaceId/title here).
    await setDoc(
        sequenceRef,
        {
            updatedAt: nowSec,
            orderedLayerIds,
        },
        { merge: true },
    );

    for (const entry of sourceEntries) {
        const layerRef = doc(sequenceRef, kSequenceLayersSubcollectionName, entry.layerId);
        await setDoc(
            layerRef,
            {
                id: entry.layerId,
                sequenceId,
                userId,
                ...(workspaceId ? { workspaceId } : {}),
                name: entry.name,
                createdAt: nowSec,
                updatedAt: nowSec,
                orderedClipIds: [entry.clipId],
            },
            { merge: true },
        );

        const clipRef = doc(layerRef, kSequenceLayerClipsSubcollectionName, entry.clipId);
        const safeDurationSec = Math.max(0.01, entry.durationSec);
        const offsetSec = earliestRecordingStartMs !== null && entry.recordingStartMs !== null
            ? Math.max(0, (entry.recordingStartMs - earliestRecordingStartMs) / 1000)
            : 0;
        await setDoc(
            clipRef,
            {
                id: entry.clipId,
                sequenceId,
                layerId: entry.layerId,
                userId,
                ...(workspaceId ? { workspaceId } : {}),
                createdAt: nowSec,
                updatedAt: nowSec,
                type: entry.type,
                assetId: entry.trackId,
                name: entry.name,
                startSec: 0,
                inSec: 0,
                outSec: safeDurationSec,
                offsetSec,
                durationSec: safeDurationSec,
                trimmedDurationSec: safeDurationSec,
                props: { playbackRate: 1, gain: 1, alpha: 1 },
                media: { hasAudioStream: Boolean(entry.track?.hasAudioStream) },
                waveformColor: null,
            },
            { merge: true },
        );
    }

    return { orderedLayerIds };
}
